import { h, Host } from "@stencil/core";
import { computePosition, autoUpdate, arrow, flip, offset, shift } from "@floating-ui/dom";
let id = 0;
export class CoreTooltip {
    constructor() {
        this.tooltipId = `tooltip-${++id}`;
        this.direction = 'top';
        this.tooltipBackgroundColor = 'color-neutral-background-strong';
        this.tooltipColor = 'color-neutral-content-medium';
        this.tooltipPadding = 'var(--number-spacing-3xsmall) var(--number-spacing-2xsmall)';
        this.open = false;
        this.trigger = undefined;
        this.popover = undefined;
    }
    async handleDirectionChange() {
        await this.handlePopover();
    }
    async handleOpenChange(newValue, oldValue) {
        if (newValue === oldValue)
            return;
        this.open = newValue;
        if (this.open) {
            await this.handlePopover();
            // to allow transition on popover opening, it is is displayed with opacity 0 before removing the hidden class
            this.popover.style.display = 'block';
            setTimeout(() => {
                this.popover.classList.remove('tooltip-content--hidden');
            }, 100);
        }
        else {
            // to allow transition on popover closing, the hidden class is added before adding display: none
            this.popover.classList.add('tooltip-content--hidden');
            setTimeout(() => {
                this.popover.style.display = 'none';
                // retrieve tooltip content from the body on close
                this.element.querySelector('.tooltip-wrapper').append(this.popover);
            }, 100);
        }
    }
    disconnectedCallback() {
        this.open = false;
    }
    async updatePosition(reference, floating) {
        const arrowEl = this.popover.querySelector('.pointer');
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        await computePosition(reference, floating, {
            // choosing placement depending on the alignment property
            placement: this.direction,
            middleware: [
                offset(8), // offset equal to the arrow size in px
                flip(),
                shift(),
                arrow({
                    element: arrowEl
                })
            ]
            // eslint-disable-next-line sonarjs/cognitive-complexity
        }).then(({ x, y, middlewareData, placement }) => {
            // TODO: refactor this function to reduce complexity
            Object.assign(floating.style, {
                top: `${y}px`,
                left: `${x}px`
            });
            // adding placement class to allow translate transition on hover
            this.popover.classList.add(`tooltip-content--${placement}`);
            // eslint-disable-next-line security/detect-object-injection
            const staticSide = {
                top: 'bottom',
                right: 'left',
                bottom: 'top',
                left: 'right'
            }[placement];
            let rotateValue = 0;
            switch (staticSide) {
                case 'bottom':
                    rotateValue = 0;
                    break;
                case 'left':
                    rotateValue = 90;
                    break;
                case 'top':
                    rotateValue = 180;
                    break;
                case 'right':
                    rotateValue = -90;
                    break;
                default:
                    rotateValue = 0;
                    break;
            }
            if (middlewareData.arrow) {
                const { x, y } = middlewareData.arrow;
                const xSides = staticSide === 'left' || staticSide === 'right';
                // apply to arrow parent element
                // @ts-expect-error
                Object.assign(arrowEl.style, {
                    left: x != null ? `${x}px` : '',
                    top: y != null ? `${y}px` : '',
                    // Ensure the static side gets unset when
                    // flipping to other placements' axes.
                    right: '',
                    bottom: '',
                    // @ts-expect-error
                    [staticSide]: `${-(xSides ? arrowEl.offsetWidth / 2 : arrowEl.offsetHeight)}px`
                });
                // apply to arrow svg element
                Object.assign(arrowEl.querySelector('svg').style, {
                    left: xSides && x != null ? `${x}px` : '',
                    top: xSides && y != null ? `${y}px` : '',
                    // @ts-expect-error
                    [staticSide]: `${-(xSides && arrowEl.offsetWidth + 2)}px`,
                    transform: `rotate(${rotateValue}deg)`
                });
            }
        });
    }
    async handlePopover() {
        const reference = this.element.querySelector('.trigger-element');
        const floating = document.getElementById(this.tooltipId);
        if (floating) {
            this.popover = floating;
            document.body.append(this.popover);
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            autoUpdate(reference, floating, async () => {
                await this.updatePosition(reference, floating);
            });
        }
    }
    componentWillLoad() {
        if (document.getElementById(this.tooltipId)) {
            document.getElementById(this.tooltipId).style.setProperty('--tooltip-background-color', `var(--${this.tooltipBackgroundColor})`);
            document.getElementById(this.tooltipId).style.setProperty('--tooltip-color', `var(--${this.tooltipColor})`);
            document.getElementById(this.tooltipId).style.setProperty('--tooltip-padding', `var(--${this.tooltipPadding})`);
        }
    }
    componentDidLoad() {
        document.getElementById(this.tooltipId).style.setProperty('--tooltip-background-color', `var(--${this.tooltipBackgroundColor})`);
        document.getElementById(this.tooltipId).style.setProperty('--tooltip-color', `var(--${this.tooltipColor})`);
        document.getElementById(this.tooltipId).style.setProperty('--tooltip-padding', `var(--${this.tooltipPadding})`);
        // on click outside close dropdown
        document.addEventListener('click', event => {
            if (!event.composedPath().includes(this.element) && !event.composedPath().includes(document.getElementById(this.tooltipId))) {
                this.open = false;
            }
        });
    }
    render() {
        return (h(Host, { key: '4b675933495073292dda2068d1aeef46c0112d62' }, h("div", { key: '71d595b43573dcddf8788ef668c18c8f5204f8de', class: `core-tooltip tooltip-wrapper ${this.open ? 'tooltip-open' : 'tooltip-closed'}`, "aria-describedby": this.tooltipId, onMouseEnter: () => {
                this.open = true;
            }, onMouseLeave: () => {
                this.open = false;
            }, onFocusin: () => {
                this.open = true;
            }, onFocusout: () => {
                this.open = false;
            } }, h("div", { key: '3b865a7d0d6aaf886f8a5085644af9d093d7fbaa', class: "trigger-element" }, h("slot", { key: 'f6cca1cba2fbcdb22a2583b5a1e1fa1325e28634', name: "trigger-element" }, 
        // Adding default information button if there is no trigger-element slot specified
        !this.element.querySelector('[slot="trigger-element"]') && (h("button", { type: "button", class: "information-button", "aria-labelledby": this.tooltipId }, h("svg", { style: { width: 'var(--number-size-icon-medium)', height: 'var(--number-size-icon-medium)' }, viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, h("circle", { cx: "12", cy: "12", r: "11", fill: "#0072E2" }), h("path", { d: "M12 7.25C12.8284 7.25 13.5 6.57843 13.5 5.75C13.5 4.92157 12.8284 4.25 12 4.25C11.1716 4.25 10.5 4.92157 10.5 5.75C10.5 6.57843 11.1716 7.25 12 7.25Z", fill: "white" }), h("path", { d: "M13 10.75C13 10.1977 12.5523 9.75 12 9.75C11.4477 9.75 11 10.1977 11 10.75V18.75C11 19.3023 11.4477 19.75 12 19.75C12.5523 19.75 13 19.3023 13 18.75V10.75Z", fill: "white" })))))), h("div", { key: 'cb17f2806a7dbc8eeb49d827ba470527584dedc8', id: this.tooltipId, class: `core-tooltip-content tooltip-content--hidden tooltip--${this.direction} typography-body-small-regular`, role: "tooltip" }, h("slot", { key: 'adbb44c496b572ba20aa93cfbdb66f22dbd7224a', name: "tooltip-content" }), h("i", { key: 'e3200bed732bc73805c27dd69542787d89480622', class: "pointer", "aria-hidden": "true" }, h("svg", { key: '39e94ae328d788660e5185e2beefaf641a6d8a12', width: "1.25rem", height: "0.5rem", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, h("g", { key: '2e246373935f968face1307caabb403b0f9b0a06', "clip-path": "url(#clip0_698_61088)" }, h("path", { key: 'b6f53e4da517f5ff9b8d98efc30589537c2da831', d: "M6.16 2.88098L8.56 6.08098C9.14223 6.85728 9.43335 7.24545 9.8156 7.31716C9.93747 7.34003 10.0625 7.34003 10.1844 7.31716C10.5666 7.24545 10.8578 6.85729 11.44 6.08098L13.84 2.88098C14.7802 1.6274 15.2503 1.00061 15.8875 0.604281C16.0977 0.473596 16.3195 0.362692 16.5501 0.272997C17.2495 0.000976562 18.033 0.000976562 19.6 0.000976562H0.4C1.96697 0.000976562 2.75046 0.000976562 3.44988 0.272997C3.68051 0.362692 3.90232 0.473596 4.11245 0.604281C4.74972 1.00061 5.21982 1.6274 6.16 2.88098Z" })), h("defs", { key: 'a41effaef6756fee9afe67daf5b08cd0b7d462cb' }, h("clipPath", { key: '34a3e5ce5122ca4a61abecb17b26ad793baf6249', id: "clip0_698_61088" }, h("rect", { key: '60afcc4ff931381ddcf238bf5e38e064ac5bc4c5', width: "20", height: "8", transform: "translate(0 0.000976562)" })))))))));
    }
    static get is() { return "core-tooltip"; }
    static get originalStyleUrls() {
        return {
            "$": ["core-tooltip.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["core-tooltip.css"]
        };
    }
    static get properties() {
        return {
            "direction": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'top' | 'right' | 'bottom' | 'left'",
                    "resolved": "\"bottom\" | \"left\" | \"right\" | \"top\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Tooltip alignment prop to where the tooltip will appear."
                },
                "attribute": "direction",
                "reflect": false,
                "defaultValue": "'top'"
            },
            "tooltipBackgroundColor": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "ColorType",
                    "resolved": "\"color-neutral-content-strong\" | \"color-neutral-content-medium\" | \"color-neutral-border-strong\" | \"color-neutral-border-medium\" | \"color-neutral-background-strong\" | \"color-neutral-background-medium\" | \"color-neutral-background-soft\" | \"color-brand-content\" | \"color-brand-decoration\" | \"color-brand-background\" | \"color-status-content-success\" | \"color-status-background-success\" | \"color-status-content-warning\" | \"color-status-background-warning\" | \"color-status-content-error\" | \"color-status-background-error\" | \"color-status-content-information\" | \"color-status-background-information\" | \"color-status-content-all-accent\" | \"color-status-background-success-accent\" | \"color-status-background-warning-accent\" | \"color-status-background-error-accent\" | \"color-status-background-information-accent\" | \"color-additional-content-color1\" | \"color-additional-background-color1\" | \"color-additional-content-color2\" | \"color-additional-background-color2\" | \"color-additional-content-color3\" | \"color-additional-background-color3\" | \"color-additional-content-color4\" | \"color-additional-background-color4\" | \"color-additional-content-all-accent\" | \"color-additional-background-color1-accent\" | \"color-additional-background-color2-accent\" | \"color-additional-background-color3-accent\" | \"color-additional-background-color4-accent\" | \"color-global-content-link\" | \"color-global-border-focus\" | \"color-global-all-disabled-strong\" | \"color-global-all-disabled-soft\" | \"color-global-overlay-medium\" | \"color-action-primary-content-default\" | \"color-action-primary-content-interacting\" | \"color-action-primary-border-default\" | \"color-action-primary-border-interacting\" | \"color-action-primary-background-default\" | \"color-action-primary-background-interacting\" | \"color-action-secondary-content-default\" | \"color-action-secondary-content-interacting\" | \"color-action-secondary-border-default\" | \"color-action-secondary-border-interacting\" | \"color-action-secondary-background-default\" | \"color-action-secondary-background-interacting\" | \"color-action-tertiary-content-default\" | \"color-action-tertiary-content-interacting\" | \"color-action-tertiary-border-default\" | \"color-action-tertiary-border-interacting\" | \"color-action-tertiary-background-default\" | \"color-action-tertiary-background-interacting\" | \"color-selection-primary-content-default\" | \"color-selection-primary-content-interacting\" | \"color-selection-primary-content-selected\" | \"color-selection-primary-border-default\" | \"color-selection-primary-border-interacting\" | \"color-selection-primary-border-selected\" | \"color-selection-primary-background-default\" | \"color-selection-primary-background-interacting\" | \"color-selection-primary-background-selected\" | \"color-selection-secondary-content-default\" | \"color-selection-secondary-content-interacting\" | \"color-selection-secondary-content-selected\" | \"color-selection-secondary-border-default\" | \"color-selection-secondary-border-interacting\" | \"color-selection-secondary-border-selected\" | \"color-selection-secondary-background-default\" | \"color-selection-secondary-background-interacting\" | \"color-selection-secondary-background-selected\" | \"color-edition-content-default\" | \"color-edition-content-interacting\" | \"color-edition-border-default\" | \"color-edition-border-interacting\" | \"color-edition-background-default\" | \"color-edition-background-interacting\"",
                    "references": {
                        "ColorType": {
                            "location": "local",
                            "path": "/Users/louismarcos/Documents/dev/core-designsystem/src/components/core-tooltip/core-tooltip.tsx",
                            "id": "src/components/core-tooltip/core-tooltip.tsx::ColorType"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Change the `background-color` of the tooltip content."
                },
                "attribute": "tooltip-background-color",
                "reflect": false,
                "defaultValue": "'color-neutral-background-strong'"
            },
            "tooltipColor": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "ColorType",
                    "resolved": "\"color-neutral-content-strong\" | \"color-neutral-content-medium\" | \"color-neutral-border-strong\" | \"color-neutral-border-medium\" | \"color-neutral-background-strong\" | \"color-neutral-background-medium\" | \"color-neutral-background-soft\" | \"color-brand-content\" | \"color-brand-decoration\" | \"color-brand-background\" | \"color-status-content-success\" | \"color-status-background-success\" | \"color-status-content-warning\" | \"color-status-background-warning\" | \"color-status-content-error\" | \"color-status-background-error\" | \"color-status-content-information\" | \"color-status-background-information\" | \"color-status-content-all-accent\" | \"color-status-background-success-accent\" | \"color-status-background-warning-accent\" | \"color-status-background-error-accent\" | \"color-status-background-information-accent\" | \"color-additional-content-color1\" | \"color-additional-background-color1\" | \"color-additional-content-color2\" | \"color-additional-background-color2\" | \"color-additional-content-color3\" | \"color-additional-background-color3\" | \"color-additional-content-color4\" | \"color-additional-background-color4\" | \"color-additional-content-all-accent\" | \"color-additional-background-color1-accent\" | \"color-additional-background-color2-accent\" | \"color-additional-background-color3-accent\" | \"color-additional-background-color4-accent\" | \"color-global-content-link\" | \"color-global-border-focus\" | \"color-global-all-disabled-strong\" | \"color-global-all-disabled-soft\" | \"color-global-overlay-medium\" | \"color-action-primary-content-default\" | \"color-action-primary-content-interacting\" | \"color-action-primary-border-default\" | \"color-action-primary-border-interacting\" | \"color-action-primary-background-default\" | \"color-action-primary-background-interacting\" | \"color-action-secondary-content-default\" | \"color-action-secondary-content-interacting\" | \"color-action-secondary-border-default\" | \"color-action-secondary-border-interacting\" | \"color-action-secondary-background-default\" | \"color-action-secondary-background-interacting\" | \"color-action-tertiary-content-default\" | \"color-action-tertiary-content-interacting\" | \"color-action-tertiary-border-default\" | \"color-action-tertiary-border-interacting\" | \"color-action-tertiary-background-default\" | \"color-action-tertiary-background-interacting\" | \"color-selection-primary-content-default\" | \"color-selection-primary-content-interacting\" | \"color-selection-primary-content-selected\" | \"color-selection-primary-border-default\" | \"color-selection-primary-border-interacting\" | \"color-selection-primary-border-selected\" | \"color-selection-primary-background-default\" | \"color-selection-primary-background-interacting\" | \"color-selection-primary-background-selected\" | \"color-selection-secondary-content-default\" | \"color-selection-secondary-content-interacting\" | \"color-selection-secondary-content-selected\" | \"color-selection-secondary-border-default\" | \"color-selection-secondary-border-interacting\" | \"color-selection-secondary-border-selected\" | \"color-selection-secondary-background-default\" | \"color-selection-secondary-background-interacting\" | \"color-selection-secondary-background-selected\" | \"color-edition-content-default\" | \"color-edition-content-interacting\" | \"color-edition-border-default\" | \"color-edition-border-interacting\" | \"color-edition-background-default\" | \"color-edition-background-interacting\"",
                    "references": {
                        "ColorType": {
                            "location": "local",
                            "path": "/Users/louismarcos/Documents/dev/core-designsystem/src/components/core-tooltip/core-tooltip.tsx",
                            "id": "src/components/core-tooltip/core-tooltip.tsx::ColorType"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Change the `color` of the tooltip content."
                },
                "attribute": "tooltip-color",
                "reflect": false,
                "defaultValue": "'color-neutral-content-medium'"
            },
            "tooltipPadding": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Change the `padding` of the tooltip content."
                },
                "attribute": "tooltip-padding",
                "reflect": false,
                "defaultValue": "'var(--number-spacing-3xsmall) var(--number-spacing-2xsmall)'"
            }
        };
    }
    static get states() {
        return {
            "open": {},
            "trigger": {},
            "popover": {}
        };
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "direction",
                "methodName": "handleDirectionChange"
            }, {
                "propName": "open",
                "methodName": "handleOpenChange"
            }];
    }
}
//# sourceMappingURL=core-tooltip.js.map
